import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  Hourglass, CheckCircle2, XCircle, CalendarDays, Users, UserMinus,
  Layers, PartyPopper, RefreshCw, ClipboardList, AlertCircle,
} from 'lucide-react';

import { StatGrid, ChartCard } from '../../components/dashboard';
import leaveService from '../../services/LeaveService';
import holidayService from '../../services/holidayService';
import {
  LEAVE_STATUSES,
  buildLeaveTypeMap, computeLeaveKpis, buildStatusBreakdown, buildDaysByType,
  buildPaidSplit, buildMonthlyTrend, buildTopEmployees, buildPendingQueue,
  upcomingHolidays, buildHolidayTypes, displayDate, fmtDays,
} from '../../utils/leaveDashboardAnalytics';

const tooltipStyle = { borderRadius: 8, fontSize: 12, border: '1px solid #e5e7eb' };
const CARD = 'bg-white border border-gray-100 rounded-xl p-5 shadow-sm';

/* Every endpoint answers { StatusCode, ResultSet }. */
const rowsOf = (res) => res?.ResultSet || res?.data?.ResultSet || [];

export default function LeaveDashboard() {
  const [byStatus, setByStatus] = useState({});
  const [types, setTypes] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // One request-list call per status, plus the two reference lists.
      const token = localStorage.getItem('auth-key') || localStorage.getItem('AuthKey') || '';
      const [typeRes, holidayRes, ...statusRes] = await Promise.all([
        leaveService.GetLeaveTypeList(),
        holidayService.getAllHolidays(token).catch(() => ({ ResultSet: [] })),
        ...LEAVE_STATUSES.map((s) =>
          leaveService.GetLeavesRequestByStatus(s).catch(() => ({ ResultSet: [] }))
        ),
      ]);

      setTypes(rowsOf(typeRes));
      setHolidays(rowsOf(holidayRes));
      setByStatus(
        LEAVE_STATUSES.reduce((acc, s, i) => ({ ...acc, [s]: rowsOf(statusRes[i]) }), {})
      );
    } catch (err) {
      setError(err?.message || 'Could not load leave data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const typeMap        = useMemo(() => buildLeaveTypeMap(types), [types]);
  const kpis           = useMemo(() => computeLeaveKpis(byStatus, types, holidays), [byStatus, types, holidays]);
  const statusDonut    = useMemo(() => buildStatusBreakdown(byStatus), [byStatus]);
  const daysByType     = useMemo(() => buildDaysByType(byStatus, typeMap), [byStatus, typeMap]);
  const paidSplit      = useMemo(() => buildPaidSplit(byStatus, typeMap), [byStatus, typeMap]);
  const monthlyTrend   = useMemo(() => buildMonthlyTrend(byStatus), [byStatus]);
  const topEmployees   = useMemo(() => buildTopEmployees(byStatus), [byStatus]);
  const pendingQueue   = useMemo(() => buildPendingQueue(byStatus, typeMap), [byStatus, typeMap]);
  const nextHolidays   = useMemo(() => upcomingHolidays(holidays, new Date(), 6), [holidays]);
  const holidayTypes   = useMemo(() => buildHolidayTypes(holidays), [holidays]);

  const stats = [
    { label: 'Pending Requests', value: kpis.pending, sub: fmtDays(kpis.pendingDays) + ' awaiting', icon: Hourglass, tone: 'amber' },
    { label: 'Approved', value: kpis.approved, sub: fmtDays(kpis.approvedDays) + ' granted', icon: CheckCircle2, tone: 'emerald' },
    { label: 'Rejected', value: kpis.rejected, sub: `${kpis.approvalRate.toFixed(0)}% approval rate`, icon: XCircle, tone: 'red' },
    { label: 'Total Days', value: fmtDays(kpis.totalDays).replace(' days', '').replace(' day', ''), sub: 'Across all requests', icon: CalendarDays, tone: 'blue' },
    { label: 'Employees', value: kpis.employees, sub: 'Have applied for leave', icon: Users, tone: 'violet' },
    { label: 'On Leave Today', value: kpis.onLeaveToday, sub: 'Approved & in range', icon: UserMinus, tone: 'orange' },
    { label: 'Leave Types', value: kpis.activeTypes, sub: `${types.length} configured`, icon: Layers, tone: 'indigo' },
    { label: 'Upcoming Holidays', value: kpis.upcomingHolidays, sub: 'From today onward', icon: PartyPopper, tone: 'sky' },
  ];

  const noRequests = kpis.totalRequests === 0;

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center h-72 gap-3 text-gray-400">
          <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading leave data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-sm font-semibold text-gray-800">Leave Overview</h1>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {kpis.totalRequests} request{kpis.totalRequests === 1 ? '' : 's'} · {kpis.activeTypes} active leave types
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* ── KPIs ────────────────────────────────────────────────────────── */}
      <StatGrid stats={stats} columns={8} />

      {/* ── Row 1: monthly trend (span 2) + status donut + paid split ───── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2">
          <ChartCard title="Leave Requests by Month" subtitle="Applications and days requested" empty={!monthlyTrend.length}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthlyTrend} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="requests" name="Requests" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={34} />
                <Bar dataKey="days" name="Days" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={34} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard
          title="Requests by Status"
          subtitle={`${kpis.totalRequests} in total`}
          empty={!statusDonut.length}
          footer={
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 text-[11px] text-gray-600">
              {statusDonut.map((d) => (
                <span key={d.name} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                  {d.name} <span className="font-semibold text-gray-800">{d.value}</span>
                </span>
              ))}
            </div>
          }
        >
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie data={statusDonut} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                {statusDonut.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Paid vs No Pay"
          subtitle="Days, by leave type"
          empty={!paidSplit.length}
          footer={
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 text-[11px] text-gray-600">
              {paidSplit.map((d) => (
                <span key={d.name} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                  {d.name} <span className="font-semibold text-gray-800">{d.value}</span>
                </span>
              ))}
            </div>
          }
        >
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie data={paidSplit} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                {paidSplit.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Row 2: days by type + top employees + holiday types ─────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Days by Leave Type" subtitle="Approved and pending" empty={!daysByType.length}>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={daysByType} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} days`, 'Days']} />
              <Bar dataKey="value" fill="#7c3aed" radius={[0, 4, 4, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top Employees by Leave" subtitle="Most days requested" empty={!topEmployees.length}>
          <div className="space-y-3.5">
            {topEmployees.map((e, i) => {
              const max = Math.max(1, ...topEmployees.map((x) => x.days));
              const isTop = i < 3;
              return (
                <div key={e.serviceNo} className="flex items-center gap-3">
                  <span className={`w-6 h-6 shrink-0 rounded-full text-white text-[11px] font-bold flex items-center justify-center
                    ${isTop ? 'bg-primary-500' : 'bg-gray-300'}`}>
                    {i + 1}
                  </span>
                  <span className={`w-24 shrink-0 text-xs truncate ${isTop ? 'text-primary-600 font-medium' : 'text-gray-600'}`}
                    title={e.serviceNo}>
                    {e.serviceNo}
                  </span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full relative">
                    <div className="absolute inset-y-0 left-0 bg-primary-500 rounded-full"
                      style={{ width: `${(e.days / max) * 100}%` }} />
                  </div>
                  <span className="w-14 text-right text-xs font-semibold text-gray-700 tabular-nums">
                    {e.days}d
                  </span>
                </div>
              );
            })}
          </div>
        </ChartCard>

        <ChartCard title="Holidays by Type" subtitle={`${holidays.length} in the calendar`} empty={!holidayTypes.length}>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={holidayTypes} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Holidays']} />
              <Bar dataKey="value" fill="#0ea5e9" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Row 3: pending queue + upcoming holidays ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={CARD}>
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList size={16} className="text-amber-600" />
            <h3 className="text-sm font-semibold text-gray-800">Pending Approvals</h3>
            {kpis.pending > 0 && (
              <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                {kpis.pending}
              </span>
            )}
          </div>
          {!pendingQueue.length ? (
            <div className="h-[160px] flex items-center justify-center text-gray-400 text-sm">
              Nothing awaiting approval
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {pendingQueue.map((r) => (
                <div key={r.formNo} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-gray-900 truncate">
                      {r.serviceNo} · {r.typeName}
                    </div>
                    <div className="text-[11px] text-gray-400 truncate">
                      {displayDate(r.from)} – {displayDate(r.to)}
                      {r.reason ? ` · ${r.reason}` : ''}
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded shrink-0">
                    {r.halfDay ? 'Half day' : `${r.days}d`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={CARD}>
          <div className="flex items-center gap-2 mb-4">
            <PartyPopper size={16} className="text-sky-600" />
            <h3 className="text-sm font-semibold text-gray-800">Upcoming Holidays</h3>
          </div>
          {!nextHolidays.length ? (
            <div className="h-[160px] flex items-center justify-center text-gray-400 text-sm">
              No upcoming holidays
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {nextHolidays.map((h) => (
                <div key={h.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-gray-900 truncate">{h.name}</div>
                    <div className="text-[11px] text-gray-400 truncate">
                      {h.type}{h.remarks ? ` · ${h.remarks}` : ''}
                    </div>
                  </div>
                  <span className="text-[11px] text-gray-500 tabular-nums shrink-0">{displayDate(h.date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {noRequests && !error && (
        <div className="text-center py-10 text-gray-400 text-sm">
          No leave requests found yet.
        </div>
      )}
    </div>
  );
}